import { createSlice, type PayloadAction } from "@reduxjs/toolkit"
import type { IWaterContentCalculator, unitId, WaterContentCriteriaOption } from "../../interfaces/interfaces";
import { apiSlice } from "./apiSlice";
import { UnitConverter } from "../../components/MeasurementUnits/UnitConverter";


const initialState: IWaterContentCalculator = {
	inputs: [
		{
			id: 'water-content-field-reservoir-pressure',
			type: 'string',
			label: '',
			// value: 100,
			queryName: 'reservoir_pressure',
			baseUnitId: 'bar',
			baseValue: 314,
			currentValue: 314,
			currentUnitId: 'bar',
			unitCategoryId: 'pressure',
			userValue: null,
			userUnitId: null,
			min: 1.01325,
			max: 500
		},
		{
			id: 'water-content-field-reservoir-temperature',
			type: 'string',
			label: '',
			// value: 32,
			queryName: 'reservoir_temperature',
			baseUnitId: 'celsius',
			baseValue: 87,
			currentValue: 87,
			currentUnitId: 'celsius',
			unitCategoryId: 'temperature',
			userValue: null,
			userUnitId: null,
		},
		{
			id: 'water-content-field-wellhead-pressure',
			type: 'string',
			label: '',
			// value: 100,
			queryName: 'wellhead_pressure',
			baseUnitId: 'bar',
			baseValue: 80,
			currentValue: 80,
			currentUnitId: 'bar',
			unitCategoryId: 'pressure',
			userValue: null,
			userUnitId: null,
		},
		{
			id: 'water-content-field-wellhead-temperature',
			type: 'string',
			label: '',
			// value: 32,
			queryName: 'wellhead_temperature',
			baseUnitId: 'celsius',
			baseValue: 25,
			currentValue: 25,
			currentUnitId: 'celsius',
			unitCategoryId: 'temperature',
			userValue: null,
			userUnitId: null,
		},
	],
	results: [
		{
			id: 'w_delta',
			type: 'string',
			label: '',
			value: null,
			queryName: 'w_delta'
		},
		{
			id: 'w_r',
			type: 'string',
			label: '',
			value: null,
			queryName: 'w_r'
		},
		{
			id: 'w_wh',
			type: 'string',
			label: '',
			value: null,
			queryName: 'w_wh'
		}
	],
	criteria: {
		id: "water-content-criteria",
		selected: "bukacek",
		options: ["bukacek", "daubert"],
	},
	downloadLink: "",
	chartConfig: {},
	requiredSubscriptionLevel: "free",
	chartsData: {
		reservoir: [],
		wellhead: [],
	}
};

interface IWCPlot {
	pressure: Record<string, number>,
	w: Record<string, number>,
}

interface IWaterContentResponse {
	results: Record<string, number>,
	plots: IWCPlot[]
	errors: string[] | null,
	warnings: string[] | null,
}

const converter = new UnitConverter();

const waterContentCalculatorSlice = createSlice({
	name: "waterContentCalculatorSlice",
	initialState,
	selectors: {
	},
	reducers: {
		setInputsData: (state, action: PayloadAction<IWaterContentCalculator>) => {
			state.inputs = action.payload.inputs;

			state.results.forEach(r => r.value = null);
			state.chartsData = {
				reservoir: [],
				wellhead: [],
			};
		},
		setInputValue: (state, action: PayloadAction<{ inputName: string, value: number | null }>) => {
			state.inputs.forEach(i => {
				if (i.queryName === action.payload.inputName) {
					i.currentValue = action.payload.value;
					i.baseValue = (i.unitCategoryId && action.payload.value != null) ? converter.convert(action.payload.value, i.currentUnitId, i.baseUnitId) : action.payload.value;
					i.userValue = action.payload.value;
					i.userUnitId = i.currentUnitId;
				}
			});

			state.results.forEach(r => r.value = null);
			state.chartsData = {
				reservoir: [],
				wellhead: [],
			};
		},
		setInputUnit: (state, action: PayloadAction<{ inputId: string, toUnitId: unitId | null }>) => {
			state.inputs.forEach(i => {
				if (i.id === action.payload.inputId) {
					i.currentValue = i.currentValue != null ? converter.convert(i.currentValue, i.currentUnitId, action.payload.toUnitId) : null;
					i.currentUnitId = action.payload.toUnitId;
					i.baseValue = i.userValue && i.userUnitId
						? converter.convert(i.userValue, i.userUnitId, i.baseUnitId)
						: converter.convert(i.currentValue ?? 0, i.currentUnitId, i.baseUnitId);

					if (i.errorParams?.["min"] && i.min) {
						i.errorParams = { min: converter.convert(i.min, i.baseUnitId, i.currentUnitId) };
					}
					if (i.errorParams?.["max"] && i.max) {
						i.errorParams = { max: converter.convert(i.max, i.baseUnitId, i.currentUnitId) };
					}
				}
			})
		},
		validateInputs: (state) => {
			state.inputs.forEach(input => {
				if (input.min != null && input.currentValue != null && input.currentValue < input.min) {
					input.error = "validation.min_value_error";
					input.errorParams = { min: input.min };
				} else if (input.max != null && input.currentValue != null && input.currentValue > input.max) {
					input.error = "validation.max_value_error";
					input.errorParams = { max: input.max };
				} else {
					input.error = null;
					input.errorParams = null;
				}
			});
		},
		setCriteria: (state, action: PayloadAction<WaterContentCriteriaOption>) => {
			state.criteria.selected = action.payload;

			state.results.forEach(r => r.value = null);
			state.chartsData = {
				reservoir: [],
				wellhead: [],
			};
		},
		insertValuesInARow: (state, action: PayloadAction<{ values: number[], startIndex: number }>) => {
			const { values, startIndex } = action.payload;

			state.inputs = state.inputs.map((input, index) => {
				const value = values[index - startIndex];
				if (value === undefined) {
					return input;
				}

				return {
					...input,
					currentValue: value,
					baseValue: input.unitCategoryId
						? converter.convert(value, input.currentUnitId, input.baseUnitId)
						: value,
					userValue: value,
					userUnitId: input.currentUnitId,
				};
			})
		}
	},
	extraReducers: (builder) => {
		builder
			.addMatcher(apiSlice.endpoints.calculator.matchFulfilled, (state, action) => {
				const params = action.meta.arg.originalArgs as Record<string, string>;
				if (!params?.calculator_type) {
					console.warn("No calculator_type in calculator response");
					return;
				}
				if (params.calculator_type != "water_content") {
					return;
				}

				const response = action.payload as IWaterContentResponse;
				if (response?.results) {
					state.results = state.results.map(r => ({
						...r,
						value: action.payload.results[r.queryName]
					}));
				}

				if (response?.plots?.[0]) {
					const reservoir = response?.plots["0"];
					const reservoirLength = Object.keys(reservoir?.pressure || {}).length;

					if (reservoirLength && reservoirLength > 0) {
						const plotData = [];
						for (let i = 0; i < reservoirLength; i++) {
							plotData.push({
								p: reservoir.pressure[i],
								w: reservoir.w[i]
							});
						}
						state.chartsData.reservoir = plotData;
					}
				}

				if (response?.plots?.[1]) {
					const wellhead = response?.plots["1"];
					const wellheadLength = Object.keys(wellhead?.pressure || {}).length;

					if (wellheadLength && wellheadLength > 0) {
						const plotData = [];
						for (let i = 0; i < wellheadLength; i++) {
							plotData.push({
								p: wellhead.pressure[i],
								w: wellhead.w[i]
							});
						}
						state.chartsData.wellhead = plotData;
					}
				}
			})
	}
})

export default waterContentCalculatorSlice

export const {
	setInputsData,
	setInputValue,
	setInputUnit,
	setCriteria,
	validateInputs,
	insertValuesInARow
} = waterContentCalculatorSlice.actions